import { Badge, Box, Text, HStack, VStack, Separator } from "@chakra-ui/react";
import { Avatar } from "../../../ui/avatar";
import { User } from "../../../../services/Dtos";
import useThemeColors from "../../../../hooks/useThemeColors";
import { genderMap } from "../../../../services/constants";
import SettingsCard from "../../../CustomUi/SettingsCard";
import CustomCode from "../../../CustomUi/CustomCode";
import { getRoleLabel } from "../../../../services/Utils";

interface Props {
  user: User;
  handleClick: () => void;
}

export default function UserCard({ user, handleClick }: Props) {
  const colors = useThemeColors();

  const userGender = genderMap[user.gender] || "Unknown";
  const fullName = `${user.firstName} ${user.lastName}`.trim();

  return (
    <SettingsCard handleClick={handleClick} reducedMotion>
      {/* Status indicator */}
      <Box
        position="absolute"
        top={3}
        right={3}
        w={3}
        h={3}
        borderRadius="full"
        bg={user.isActive ? "green.400" : "red.400"}
        border="2px solid"
        borderColor={colors.background.tertiary}
      />

      {/* Responsive Layout */}
      <HStack
        gap={{ base: 3, md: 6 }}
        align="center"
        justify="space-between"
        flexDir={{ base: "column", lg: "row" }}
        alignItems={{ base: "stretch", lg: "center" }}
        wrap="wrap"
      >
        {/* Avatar and Name Section */}
        <HStack gap={4} flex={1} minW="0">
          <Avatar
            name={fullName}
            src={user?.avatarUrl || ""}
            _hover={{
              focusRingColor: colors.schoolAccent.primary,
              ring: 2,
            }}
            transition="all 0.2s"
            flexShrink={0}
            size="lg"
            shape="rounded"
          />

          <VStack align="flex-start" gap={1} flex={1} minW="0">
            <Text
              fontWeight="semibold"
              fontSize="md"
              maxW={{ base: "100%", md: "200px" }}
            >
              {fullName || "N/A"}
            </Text>
            <Text
              fontSize="sm"
              color={colors.text.secondary}
              maxW={{ base: "100%", md: "200px" }}
              truncate
            >
              {user.email}
            </Text>
          </VStack>
        </HStack>
        <Separator />
        {/* Info Section - Horizontal on desktop, vertical on mobile */}
        <HStack gap={{ base: 2, md: 6 }} align="center" justify="space-between">
          {/* Role */}
          <VStack minW={{ base: "auto", md: "100px" }} align={"start"}>
            <Text fontSize="sm" color={colors.text.secondary}>
              Role
            </Text>
            <Badge
              variant="outline"
              colorPalette={"gray"}
              size={{ base: "sm", md: "md" }}
            >
              {getRoleLabel(user.role)}
            </Badge>
          </VStack>

          {/* Gender */}
          <VStack minW={{ base: "auto", md: "100px" }} align={"start"}>
            <Text fontSize="sm" color={colors.text.secondary}>
              Gender
            </Text>
            <Text fontSize="sm" fontWeight="medium">
              {userGender}
            </Text>
          </VStack>

          {/* Status */}
          <VStack minW={{ base: "auto", md: "100px" }} align={"start"}>
            <Text fontSize="sm" color={colors.text.secondary}>
              Status
            </Text>
            <Badge
              variant="outline"
              colorPalette={user.isAccountActivated ? "green" : "red"}
              size={{ base: "sm", md: "md" }}
            >
              {user.isAccountActivated ? "Active" : "Inactive"}
            </Badge>
          </VStack>
        </HStack>
        <HStack>
          {" "}
          {/* User ID */}
          <VStack minW={{ base: "auto", md: "100px" }} align={"start"}>
            <Text fontSize="xs" color={colors.text.tertiary}>
              User ID
            </Text>
            <CustomCode value={user.id} />
          </VStack>
        </HStack>
      </HStack>
    </SettingsCard>
  );
}
